import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text'
import { $createParagraphNode } from 'lexical'
import {
  activePlugins$,
  allowedHeadingLevels$,
  BlockType,
  convertSelectionToNode$,
  currentBlockType$,
  useCellValue,
  usePublisher,
  useTranslation,
} from '@mdxeditor/editor'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'

/**
 * A toolbar component that allows the user to change the block type of the current selection.
 * Supports paragraphs, headings and block quotes.
 * @group Toolbar Components
 */
export const CustomBlockTypeSelect = () => {
  const convertSelectionToNode = usePublisher(convertSelectionToNode$)
  const currentBlockType = useCellValue(currentBlockType$)
  const activePlugins = useCellValue(activePlugins$)
  const hasQuote = activePlugins.includes('quote')
  const hasHeadings = activePlugins.includes('headings')
  const t = useTranslation()

  if (!hasQuote && !hasHeadings) {
    return null
  }
  const items: { label: string | JSX.Element; value: BlockType }[] = [
    { label: t('toolbar.blockTypes.paragraph', 'Paragraph'), value: 'paragraph' },
  ]

  if (hasQuote) {
    items.push({ label: t('toolbar.blockTypes.quote', 'Quote'), value: 'quote' })
  }

  if (hasHeadings) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const allowedHeadingLevels = useCellValue(allowedHeadingLevels$)
    items.push(
      ...allowedHeadingLevels.map(
        (n) => ({ label: t('toolbar.blockTypes.heading', 'Heading {{level}}', { level: n }), value: `h${n}` }) as const
      )
    )
  }

  // Only use currentBlockType as value if it matches one of the available items
  const availableValues = items.map((item) => item.value)
  const selectValue = availableValues.includes(currentBlockType) ? currentBlockType : undefined

  return (
    <Select
      value={selectValue}
      onValueChange={(blockType: BlockType) => {
        switch (blockType) {
          case 'quote':
            convertSelectionToNode(() => $createQuoteNode())
            break
          case 'paragraph':
            convertSelectionToNode(() => $createParagraphNode())
            break
          case '':
            break
          default:
            if (blockType.startsWith('h')) {
              convertSelectionToNode(() => $createHeadingNode(blockType as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'))
            } else {
              throw new Error(`Unknown block type: ${blockType}`)
            }
        }
      }}
    >
      <SelectTrigger
        className="h-7 w-44"
        title={t('toolbar.blockTypeSelect.selectBlockTypeTooltip', 'Select block type')}
      >
        <SelectValue placeholder={t('toolbar.blockTypeSelect.placeholder', 'Block type')} />
      </SelectTrigger>
      <SelectContent>
        {items.map((item) => (
          <SelectItem key={item.value} value={item.value}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
